import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useKonamiSequence } from '../../hooks/useKonamiSequence';

// Calendar order — January through December. The SEASONAL_ACCESSORIES
// array happens to be authored in this order already, but capturing
// the parade order separately makes the intent explicit: the parade
// always walks the year, regardless of any future re-ordering of
// the source array (which is sorted for the runtime priority resolver,
// not for display).
const PARADE_ORDER = [
  'newyear', 'winter', 'valentines', 'spring', 'easter',
  'summer',  'autumn', 'halloween',  'bonfire', 'christmas',
];

const STEP_MS = 800;     // dwell per accessory
const TOTAL_MS = STEP_MS * PARADE_ORDER.length; // 8000ms

const TurboParadeContext = createContext({
  paradeActiveAccessoryId: null,
  isRunning: false,
});

// Read the current parade state from any TurboIdle (or any other
// consumer that wants to override its visual based on the parade).
export const useTurboParade = () => useContext(TurboParadeContext);

// Wraps the Alchemy app. Listens for the T-U-R-B-O konami sequence and
// cycles the paradeActiveAccessoryId through all ten seasons. Renders
// a small status caption via portal while the parade is running.
//
// Supplier portal pages never render TurboIdle, so even though the
// listener is global, the visual only appears on admin surfaces.
//
// Reduced-motion: parade still triggers (user-invoked action), but
// the implementation is already an instant per-accessory swap (no
// crossfade), so there's nothing to disable visually. We keep the
// 800ms dwell so the user can still read the caption labels.
export function TurboParadeProvider({ children }) {
  const [activeIndex, setActiveIndex] = useState(null); // null = not running
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  }, []);

  const runParade = useCallback(() => {
    clearTimers();
    // Step through the calendar order; each scheduled timeout flips
    // to the next index. After the last step a tidy-up timeout
    // clears the active index back to null so TurboIdle falls back
    // to its useSeasonalAccessory result.
    setActiveIndex(0);
    for (let i = 1; i < PARADE_ORDER.length; i++) {
      timersRef.current.push(setTimeout(() => setActiveIndex(i), i * STEP_MS));
    }
    timersRef.current.push(setTimeout(() => {
      setActiveIndex(null);
      timersRef.current = [];
    }, TOTAL_MS));
  }, [clearTimers]);

  // Clean up any in-flight timers on unmount so a provider remount
  // (e.g. during a hot reload) doesn't leave timers running.
  useEffect(() => () => clearTimers(), [clearTimers]);

  // Konami listener — fires runParade only when the buffer matches
  // exactly. running=true freezes the buffer while a parade is in
  // flight so back-to-back triggers don't double-fire.
  // Re-keyed T-U-R-B-O → O-T-T-O for Otto (parade behaviour unchanged).
  useKonamiSequence('otto', runParade, { running: activeIndex !== null });

  const isRunning = activeIndex !== null;
  const paradeActiveAccessoryId = isRunning ? PARADE_ORDER[activeIndex] : null;

  return (
    <TurboParadeContext.Provider value={{ paradeActiveAccessoryId, isRunning }}>
      {children}
    </TurboParadeContext.Provider>
  );
}

export default TurboParadeProvider;
